import {useState, useEffect} from 'react';
import type NetworkOptimizationStore from '../../../lib/store';
import handleError from '../../../../../lib/error';

interface UseDisruptionAnalysisDataLoadingParams {
    networkOptimizationStore: NetworkOptimizationStore;
}

interface UseDisruptionAnalysisDataLoadingReturn {
    loading: boolean;
    error: string | null;
}

/**
 * Hook to manage disruption analysis data loading
 * Loads data when activeTab === 3 and filters change
 */
export function useDisruptionAnalysisDataLoading({
    networkOptimizationStore
}: UseDisruptionAnalysisDataLoadingParams): UseDisruptionAnalysisDataLoadingReturn {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const controller = new AbortController();

        if (!networkOptimizationStore.disruptionAnalysisSpecialtyFilter) {
            setLoading(false);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);

        const loadData = async () => {
            try {
                await networkOptimizationStore.loadDisruptionAnalysisLocalProviders(controller);
                networkOptimizationStore.deselectAllAdditionalProviders();
                networkOptimizationStore.selectAllExistingProviders();
                setLoading(false);
            } catch (err) {
                const parsedError = handleError(err as Error, null, 'Failed to load disruption analysis provider data');
                if (!parsedError.isAbortError) {
                    setError(parsedError.message);
                    setLoading(false);
                }
            }
        };

        // Only load when activeTab is 3 (Disruption Analysis tab)
        if (networkOptimizationStore.activeTab === 3) {
            loadData();
        }

        return () => controller.abort();
    }, [
        networkOptimizationStore.disruptionAnalysisSpecialtyFilter,
        networkOptimizationStore.activeTab,
        networkOptimizationStore.appliedDisruptionAnalysisFilter.standard_type,
        networkOptimizationStore.appliedDisruptionAnalysisFilter.patient_roster_name,
        networkOptimizationStore.appliedDisruptionAnalysisFilter.state,
        networkOptimizationStore.appliedDisruptionAnalysisFilter.county,
        networkOptimizationStore.appliedDisruptionAnalysisFilter.zip,
        networkOptimizationStore.runId,
        networkOptimizationStore.revisionId,
    ]);

    return {
        loading,
        error
    };
}
